import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getCurrentUserId,
  getDealApplicationId,
  getHasAcknowledgedSpanishPrompt,
  getHasPreferredLanguageOverride,
  getLoanNumber,
  getNhDealApplicationId,
  selectPreferredLanguage,
  setHasAcknowledgedSpanishPrompt,
  setHasPreferredLanguageOverride,
  setPreferredLanguage,
} from "features/authSlice";
import { isPostLosLoan, selectIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import { selectIsTranslationServiceFailed } from "features/translationServiceSlice";
import PostLosLockModal from "./post-los-lock-modal/PostLosLockModal";
import TranslationErrorModal from "./translation-error-modal/TranslationErrorModal";
import LanguageToggle from "./language-toggle/LanguageToggle";
import EnglishRevertModal from "./english-revert-modal/EnglishRevertModal";
import SpanishAcknowledgmentModal, { TabType } from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES, TRANSLATION_ERROR_LABEL } from "utilities/Constant";
import { deriveApplicationStep } from "utilities/derive-application-step";
import { trackFSLanguagePreferenceChanged } from "utilities/full-story";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  buildSyncPreferredLanguageRequest,
  getExplicitUserLanguage,
  getPersistedPreferredLanguage,
  getPreferredLanguage,
  hasExplicitUserLanguageSelection,
  hasPreferredLanguageDealSyncCompleted,
  isSpanishLanguage,
  markExplicitUserLanguageSelection,
  markPreferredLanguageDealSyncCompleted,
  normalizeToAppLanguage,
  resolveDisplayedPreferredLanguage,
  selectActiveLoan,
  setPersistedPreferredLanguage,
  toUserDbPreferredLanguage,
} from "utilities/getPreferredLanguage";
import { useGetMyLoansQuery } from "pages/application/applicationService";
import { useGetBorrowerDetailsQuery } from "pages/dashboard/home/dashboardService";
import { useSyncPreferredLanguageMutation, useUpdateUserPreferredLanguageMutation } from "pages/authentication/userService";

interface PreferredLanguageProps {
  readonly isMobile?: boolean;
  readonly isAuthenticated?: boolean;
}

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const getLangPrefCookie = (): string | null => {
  const rawCookie = document.cookie;
  if (!rawCookie?.trim()) {
    return null;
  }

  const prefix = `${LANG_PREF_COOKIE_NAME}=`;
  const cookie = rawCookie
    .split(";")
    .map((part) => part?.trim())
    .find((part) => part?.startsWith(prefix));

  if (!cookie) {
    return null;
  }

  const value = cookie.slice(prefix.length).trim();
  return value || null;
};

const syncLangPrefCookie = (language: string): void => {
  if (isSpanishLanguage(language)) {
    setLangPrefCookie(SPANISH_LANG_CODE);
    return;
  }

  clearLangPrefCookie();
};

const getCookiePreferredLanguage = (): string => {
  const cookieValue = getLangPrefCookie();
  if (cookieValue === SPANISH_LANG_CODE || isSpanishLanguage(cookieValue)) {
    return "Español";
  }

  return DEFAULT_LANGUAGE;
};

const refreshPageForTranslation = (): void => {
  globalThis.location.reload();
};

const shouldReloadForLanguageChange = (nextLanguage: string): boolean => {
  return getCookiePreferredLanguage() !== normalizeToAppLanguage(nextLanguage);
};

const PreferredLanguage: React.FC<PreferredLanguageProps> = ({ isMobile = false, isAuthenticated = false }) => {
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const { pathname } = useLocation();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const hasAcknowledgedSpanishPrompt = useSelector(getHasAcknowledgedSpanishPrompt);
  const hasPreferredLanguageOverride = useSelector(getHasPreferredLanguageOverride);
  const [syncPreferredLanguage] = useSyncPreferredLanguageMutation();
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();
  const [showPostLosLockModal, setShowPostLosLockModal] = useState(false);
  const [showTranslationErrorModal, setShowTranslationErrorModal] = useState(false);
  const [isPersistingPreferredLanguage, setIsPersistingPreferredLanguage] = useState(false);
  const learnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const translationLearnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const postLoginSyncDoneRef = useRef(false);
  const dealLanguageSeedDoneRef = useRef<string | null>(null);
  const currentLoanNumber = useSelector(getLoanNumber);
  const isAllPostLOS = useSelector(selectIsLanguageToggleLocked);
  const isMyLoansPage = pathname.startsWith("/my-loans");
  const hasApplicationContext = Boolean(dealApplicationId?.trim() || nhDealApplicationId?.trim());
  const isCurrentLoanPostLos = isPostLosLoan(currentLoanNumber);
  const isPostLosLoanContext = isMyLoansPage ? isAllPostLOS : isCurrentLoanPostLos;
  const isTranslationFailed = useSelector(selectIsTranslationServiceFailed);
  const isExplicitSelection = hasPreferredLanguageOverride || hasExplicitUserLanguageSelection();
  const isPreferredLanguageEnabled = Boolean(featureFlag?.preferredLanguageEnabled);

  const { data: myLoansData } = useGetMyLoansQuery(userId, {
    skip: !isPreferredLanguageEnabled || !isAuthenticated || !userId,
  });
  const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !isPreferredLanguageEnabled || !isAuthenticated || !userId,
  });
  const activeLoan = selectActiveLoan(myLoansData, nhDealApplicationId);

  const [userLanguage, setUserLanguage] = useState<string>(() => getPreferredLanguage());
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);

  useEffect(() => { // NOSONAR
    if (!isPreferredLanguageEnabled) {
      return;
    }

    if (!isAuthenticated) {
      const savedLanguage = getPersistedPreferredLanguage();
      setUserLanguage(getPreferredLanguage(savedLanguage ?? reduxLanguage));
      return;
    }

    if (!isExplicitSelection && (!userProfile || !activeLoan)) {
      return;
    }

    if (isMyLoansPage && userProfile === undefined) {
      return;
    }

    const resolved = resolveDisplayedPreferredLanguage({
      isExplicitSelection,
      isMyLoansPage,
      hasApplicationContext,
      userDbLanguage: userProfile?.preferredLanguage,
      activeLoan,
      reduxLanguage,
    });

    const isActiveLoanPostLos = isPostLosLoan(activeLoan?.loanNumber);

    const shouldPromptSpanishAcknowledgment =
      activeLoan &&
      hasApplicationContext &&
      Boolean(activeLoan?.showAcknowledgementModal) &&
      !isActiveLoanPostLos &&
      !isExplicitSelection &&
      !hasAcknowledgedSpanishPrompt &&
      !showAcknowledgmentModal;

    if (!shouldPromptSpanishAcknowledgment) {
      if (resolved !== userLanguage) {
        setUserLanguage(resolved);
        if (!isExplicitSelection) {
          setPersistedPreferredLanguage(resolved);
          dispatch(setPreferredLanguage(resolved));
        }
      }

      if (
        activeLoan &&
        userId &&
        !isExplicitSelection &&
        hasApplicationContext &&
        activeLoan?.dealPreferredLanguage?.trim() &&
        !isActiveLoanPostLos
      ) {
        const dealLanguage = toUserDbPreferredLanguage(
          normalizeToAppLanguage(activeLoan.dealPreferredLanguage),
        );
        const userDbLanguage = userProfile?.preferredLanguage?.trim()
          ? toUserDbPreferredLanguage(normalizeToAppLanguage(userProfile.preferredLanguage))
          : null;
        const seedKey = `${userId}:${activeLoan.nhDealApplicationId}:${dealLanguage}`;

        if (userDbLanguage === dealLanguage) {
          dealLanguageSeedDoneRef.current = seedKey;
        } else if (!userDbLanguage && dealLanguageSeedDoneRef.current !== seedKey) {
          dealLanguageSeedDoneRef.current = seedKey;
          void updateUserPreferredLanguage({
            userId,
            preferredLanguage: dealLanguage,
          });
        }
      }

      if (shouldReloadForLanguageChange(resolved)) {
        if (!isExplicitSelection) {
          setPersistedPreferredLanguage(resolved);
          dispatch(setPreferredLanguage(resolved));
        }
        syncLangPrefCookie(resolved);
        refreshPageForTranslation();
        return;
      }
    }

    if (shouldPromptSpanishAcknowledgment) {
      setPendingLanguage(resolved);
      setShowAcknowledgmentModal(true);
    }
  }, [
    activeLoan,
    userProfile?.preferredLanguage,
    reduxLanguage,
    isAuthenticated,
    hasPreferredLanguageOverride,
    isExplicitSelection,
    isPreferredLanguageEnabled,
    isMyLoansPage,
    hasApplicationContext,
    hasAcknowledgedSpanishPrompt,
    dispatch,
    updateUserPreferredLanguage,
    userId,
  ]);

  useEffect(() => {
    if (!isPreferredLanguageEnabled) {
      return;
    }

    if (!isAuthenticated || !userId || !hasExplicitUserLanguageSelection()) {
      return;
    }
    if (hasPreferredLanguageOverride || hasPreferredLanguageDealSyncCompleted()) {
      return;
    }
    if (postLoginSyncDoneRef.current) {
      return;
    }

    const savedLanguage = getExplicitUserLanguage();
    if (!savedLanguage) {
      return;
    }

    postLoginSyncDoneRef.current = true;
    dispatch(setPreferredLanguage(savedLanguage));
    dispatch(setHasPreferredLanguageOverride(true));

    const syncRequest = buildSyncPreferredLanguageRequest(userId, savedLanguage);

    setIsPersistingPreferredLanguage(true);
    void syncPreferredLanguage(syncRequest)
      .then((response) => {
        if (!("error" in response)) {
          markPreferredLanguageDealSyncCompleted();
        }
      })
      .finally(() => {
        setIsPersistingPreferredLanguage(false);
      });
  }, [
    isAuthenticated,
    userId,
    dispatch,
    syncPreferredLanguage,
    hasPreferredLanguageOverride,
    isPreferredLanguageEnabled,
  ]);

  useEffect(() => {
    if (!isPreferredLanguageEnabled) {
      return;
    }

    const savedLanguage = getPersistedPreferredLanguage();
    syncLangPrefCookie(savedLanguage ?? DEFAULT_LANGUAGE);
  }, [isPreferredLanguageEnabled]);

  useEffect(() => {
    if (!isPreferredLanguageEnabled) {
      return;
    }

    if (!isTranslationFailed) {
      return;
    }

    clearLangPrefCookie();
    setUserLanguage((current) => (isSpanishLanguage(current) ? DEFAULT_LANGUAGE : current));
  }, [isTranslationFailed, isPreferredLanguageEnabled]);

  if (!isPreferredLanguageEnabled) {
    return null;
  }

  const applyLanguageState = (value: string) => {
    setUserLanguage(value);
    setPersistedPreferredLanguage(value);

    if (isAuthenticated) {
      dispatch(setPreferredLanguage(value));
      dispatch(setHasPreferredLanguageOverride(true));
    }
    syncLangPrefCookie(value);
  };

  const persistLanguageChange = async (value: string): Promise<boolean> => {
    if (!isAuthenticated || !userId) {
      return false;
    }

    const syncRequest = buildSyncPreferredLanguageRequest(userId, value);

    setIsPersistingPreferredLanguage(true);
    try {
      const response = await syncPreferredLanguage(syncRequest);
      const didPersist = !("error" in response);
      if (didPersist) {
        markPreferredLanguageDealSyncCompleted();
      } else {
        setIsPersistingPreferredLanguage(false);
      }
      return didPersist;
    } catch {
      setIsPersistingPreferredLanguage(false);
      return false;
    }
  };

  const commitLanguageChange = async (value: string): Promise<void> => {
    markExplicitUserLanguageSelection();
    const shouldReload = shouldReloadForLanguageChange(value);
    applyLanguageState(value);

    if (!isAuthenticated) {
      if (shouldReload) {
        refreshPageForTranslation();
      }
      return;
    }

    postLoginSyncDoneRef.current = true;
    const didPersist = await persistLanguageChange(value);
    if (didPersist && shouldReload) {
      refreshPageForTranslation();
      return;
    }
    setIsPersistingPreferredLanguage(false);
  };

  const handleLanguageChange = (value: string) => {
    if (isPostLosLoanContext) {
      return;
    }
    setPendingLanguage(value);
    if (value == "Español") {
      setShowAcknowledgmentModal(true);
    } else {
      setShowEnglishRevertModal(true);
    }
  };

  const getLanguageTrackingProps = (
    languageToApply: string,
    acknowledgmentLanguage?: TabType,
  ) => {
    const fromLanguage = toUserDbPreferredLanguage(userLanguage) as "Spanish" | "English";
    const toLanguage = toUserDbPreferredLanguage(languageToApply) as "Spanish" | "English";

    return {
      fromLanguage,
      toLanguage,
      applicationStep: deriveApplicationStep(pathname),
      applicationId: dealApplicationId?.trim() ? dealApplicationId : null,
      loanNumber: currentLoanNumber?.trim() ? currentLoanNumber : null,
      changeSource: "user_selector" as const,
      acknowledgmentLanguage,
    };
  };

  const handleAcknowledge = (acknowledgmentLanguage: TabType) => {
    const languageToApply = pendingLanguage ?? "Español";
    trackFSLanguagePreferenceChanged(
      getLanguageTrackingProps(languageToApply, acknowledgmentLanguage),
    );
    setPendingLanguage(null);
    if (isAuthenticated) {
      dispatch(setHasAcknowledgedSpanishPrompt(true));
    }
    setShowAcknowledgmentModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleAcknowledgmentCancel = async () => {
    const languageToRetain = userLanguage;
    setShowAcknowledgmentModal(false);
    setPendingLanguage(null);

    if (!isAuthenticated || !userId) {
      return;
    }

    dispatch(setHasAcknowledgedSpanishPrompt(true));
    applyLanguageState(languageToRetain);
    postLoginSyncDoneRef.current = true;
    await persistLanguageChange(languageToRetain);
    setIsPersistingPreferredLanguage(false);
  };

  const handleRevertConfirm = () => {
    const languageToApply = pendingLanguage ?? DEFAULT_LANGUAGE;
    trackFSLanguagePreferenceChanged(getLanguageTrackingProps(languageToApply));
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleRevertCancel = () => {
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
  };
  const handleClosePostLosLockModal = () => {
    setShowPostLosLockModal(false);
  };

  const getLockedClickHandler = (): (() => void) | undefined => {
    if (isMobile && isTranslationFailed) {
      return () => setShowTranslationErrorModal(true);
    }
    if (isMobile && isPostLosLoanContext) {
      return () => setShowPostLosLockModal(true);
    }
    return undefined;
  };

  return (
    <>
      <div className="preferred-language">
        {isTranslationFailed && !isMobile && (
          <div className="preferred-language__error-details">
            <span className="preferred-language__error-label GlobalLinkNoTx">{TRANSLATION_ERROR_LABEL}</span>
            <button
              ref={translationLearnMoreButtonRef}
              type="button"
              className="preferred-language__learn-more-button"
              onClick={() => setShowTranslationErrorModal(true)}
            >
              Learn More
            </button>
          </div>
        )}
        {isPostLosLoanContext && !isTranslationFailed && !isMobile && (
          <div className="preferred-language__lock-details">
            <span className="preferred-language__lock-label">Language Locked</span>
            <button
              ref={learnMoreButtonRef}
              type="button"
              className="preferred-language__learn-more-button"
              onClick={() => setShowPostLosLockModal(true)}
            >
              Learn More
            </button>
          </div>
        )}
        <LanguageToggle
          value={userLanguage}
          onChange={handleLanguageChange}
          options={SUPPORTED_LANGUAGES}
          isMobile={isMobile}
          disabled={isPostLosLoanContext || isPersistingPreferredLanguage}
          showTranslationError={isTranslationFailed}
          onLockedClick={getLockedClickHandler()}
        />

      </div>
      {isAuthenticated && isPersistingPreferredLanguage && (
        <LoadingOverlay text="Updating your language preference..." />
      )}
      {showAcknowledgmentModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleAcknowledge}
          onCancel={handleAcknowledgmentCancel}
        />
      )}
      {showEnglishRevertModal && (
        <EnglishRevertModal onConfirm={handleRevertConfirm} onCancel={handleRevertCancel} />
      )}
      {showPostLosLockModal && (
        <PostLosLockModal onClose={handleClosePostLosLockModal} returnFocusRef={learnMoreButtonRef} />
      )}
      {showTranslationErrorModal && (
        <TranslationErrorModal
          onClose={() => setShowTranslationErrorModal(false)}
          returnFocusRef={translationLearnMoreButtonRef}
        />
      )}
    </>
  );
};

export default PreferredLanguage;
